/* eslint-disable @typescript-eslint/no-explicit-any, no-empty */
import { doc, getDoc, getDocFromServer, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import type { CycleRing } from "@/lib/cycleTypes";
import { db } from "@/lib/firebase";

const getDb = () => db;

export type CycleData = {
  rings: CycleRing[];
  hiddenRings?: string[];
  updatedAt?: string;
};

const CYCLE_DOC_PREFIX = "cycles-";
const LOCAL_CYCLE_KEY_PREFIX = "cycle-data:";

export const getCycleDocId = (viewId: string, groupId?: string | null): string =>
  `${CYCLE_DOC_PREFIX}${viewId}${groupId ? `-${groupId}` : ''}`;

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs = 4000): Promise<T> =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Firestore request timed out."));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

const getCycleSnapshot = async (cycleDocId: string) => {
  const db = getDb();
  if (!db) throw new Error("Firestore is not configured.");
  const docRef = doc(db, "cycleSets", cycleDocId);
  try {
    return await withTimeout(getDocFromServer(docRef));
  } catch {
    return await withTimeout(getDoc(docRef));
  }
};

const emitIntent = (_name: string, _detail?: any) => {
  // diagnostics removed
};

const loadLocalCycles = (storageKey: string): CycleData | null => {
  const stored = localStorage.getItem(`${LOCAL_CYCLE_KEY_PREFIX}${storageKey}`);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as CycleData;
    return normalizeCycleData(parsed);
  } catch {
    return null;
  }
};

const saveLocalCycles = (storageKey: string, data: CycleData) => {
  localStorage.setItem(`${LOCAL_CYCLE_KEY_PREFIX}${storageKey}`, JSON.stringify(data));
};

// Strip runtime-only fields (rotation/currentStage) to avoid write storms while
// still persisting structural ring data. Runtime values are derived from the
// timeline and do not need to be written back.
const sanitizeRing = (ring: CycleRing) => ({
  id: ring.id,
  name: ring.name,
  ...(ring.description !== undefined ? { description: ring.description } : {}),
  ...(Array.isArray(ring.stageIconImageUrls) ? { stageIconImageUrls: ring.stageIconImageUrls } : {}),
  ...(ring.cycleGroup !== undefined ? { cycleGroup: ring.cycleGroup } : {}),
  stages: ring.stages,
  labels: ring.labels,
  colors: ring.colors,
  ...(ring.parentId !== undefined ? { parentId: ring.parentId } : {}),
  ...(ring.stagesPerParent !== undefined ? { stagesPerParent: ring.stagesPerParent } : {}),
  ...(ring.displayOrder !== undefined ? { displayOrder: ring.displayOrder } : {}),
  ...(ring.skinMode !== undefined ? { skinMode: ring.skinMode } : {}),
  ...(ring.themeId !== undefined ? { themeId: ring.themeId } : {}),
  ...(ring.waveform !== undefined ? { waveform: ring.waveform } : {}),
  ...(ring.waveformIntervalMultiplier !== undefined
    ? { waveformIntervalMultiplier: ring.waveformIntervalMultiplier }
    : {}),
  ...(ring.waveformIntervalDivider !== undefined
    ? { waveformIntervalDivider: ring.waveformIntervalDivider }
    : {}),
  ...(ring.useCurrentStageAsValue !== undefined
    ? { useCurrentStageAsValue: ring.useCurrentStageAsValue }
    : {}),
});

let _lastWriteLogTs = 0;
const logFirestoreWrite = (detail: Record<string, unknown>) => {
  const now = Date.now();
  // Throttle console spam: log at most once per 1s
  if (now - _lastWriteLogTs < 1000) return;
  _lastWriteLogTs = now;
  try {
    console.info('[firestore:write] cycleStore', detail);
  } catch {
    // ignore logging errors
  }
};

const normalizeCycleData = (data: CycleData | null): CycleData | null => {
  if (!data) return null;
  const rings = (Array.isArray(data.rings) ? data.rings : [])
    .filter((ring): ring is CycleRing => Boolean(ring && typeof ring === 'object' && 'id' in ring))
    .map((ring) => sanitizeRing(ring));
  const hiddenRings = Array.isArray(data.hiddenRings) ? data.hiddenRings : [];
  return {
    rings,
    hiddenRings,
    updatedAt: data.updatedAt,
  };
};

export async function loadCycleData(cycleDocId: string): Promise<CycleData | null> {
  if (typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1') {
    try { emitIntent('loadCycleData_blocked_local', { cycleDocId }); } catch {}
    return loadLocalCycles(cycleDocId);
  }
  const db = getDb();
  if (!db) {
    return loadLocalCycles(cycleDocId);
  }
  const snapshot = await getCycleSnapshot(cycleDocId);
  if (!snapshot.exists()) return null;
  const data = snapshot.data() as CycleData;
  return normalizeCycleData(data);
}

// Track last saved payload per doc to prevent duplicate writes
const _lastSavedPayload: Record<string, string> = {};
const _pendingPayload: Record<string, string | undefined> = {};

const toStablePayload = (data: { rings: ReturnType<typeof sanitizeRing>[]; hiddenRings: string[] }) => {
  const sortedRings = [...data.rings].sort((a, b) => {
    const orderA = (a as any).displayOrder ?? 0;
    const orderB = (b as any).displayOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return String(a.id).localeCompare(String(b.id));
  });
  const hidden = [...(data.hiddenRings ?? [])].sort();
  return JSON.stringify({ rings: sortedRings, hiddenRings: hidden });
};

export async function saveCycleData(cycleDocId: string, data: CycleData): Promise<void> {
  // Check disableFirestore flag first
  if (typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1') {
    const sanitized = {
      rings: data.rings.map(sanitizeRing),
      hiddenRings: data.hiddenRings ?? [],
    };
    try { emitIntent('saveCycleData_blocked_local', { cycleDocId }); } catch {}
    saveLocalCycles(cycleDocId, {
      ...sanitized,
      updatedAt: new Date().toISOString(),
    });
    return;
  }

  const sanitized = {
    rings: data.rings.map(sanitizeRing),
    hiddenRings: data.hiddenRings ?? [],
  };
  const db = getDb();
  const currentPayload = toStablePayload(sanitized);
  if (_lastSavedPayload[cycleDocId] === currentPayload) {
    try { emitIntent('saveCycleData_skipped_duplicate', { cycleDocId }); } catch {}
    return; // Skip write - no actual changes
  }
  if (_pendingPayload[cycleDocId] === currentPayload) {
    return; // Skip - identical payload already in flight
  }
  if (!db) {
    _pendingPayload[cycleDocId] = currentPayload;
    _lastSavedPayload[cycleDocId] = currentPayload;
    saveLocalCycles(cycleDocId, {
      ...sanitized,
      updatedAt: new Date().toISOString(),
    });
    delete _pendingPayload[cycleDocId];
    return;
  }
  
  try {
    _pendingPayload[cycleDocId] = currentPayload;
    logFirestoreWrite({ cycleDocId, action: 'setDoc', hasDb: Boolean(db) });
    await setDoc(
      doc(db, "cycleSets", cycleDocId),
      {
        ...sanitized,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    _lastSavedPayload[cycleDocId] = currentPayload;
    delete _pendingPayload[cycleDocId];
    try { emitIntent('saveCycleData_setdoc', { cycleDocId }); } catch {}
  } catch (err) {
    delete _pendingPayload[cycleDocId];
    try { emitIntent('saveCycleData_error', { cycleDocId, error: String(err) }); } catch {}
    throw err;
  }
}

export function subscribeCycleData(
  cycleDocId: string,
  onData: (data: CycleData | null) => void,
  onError?: (error: Error) => void
): (() => void) | null {
  if (typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1') {
    try { emitIntent('subscribeCycleData_blocked_local', { cycleDocId }); } catch {}
    onData(loadLocalCycles(cycleDocId));
    return null;
  }
  const db = getDb();
  if (!db) {
    onData(loadLocalCycles(cycleDocId));
    return null;
  }
  const docRef = doc(db, "cycleSets", cycleDocId);
  return onSnapshot(
    docRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        onData(null);
        return;
      }
      const data = snapshot.data() as CycleData;
      onData(normalizeCycleData(data));
    },
    (error) => {
      onError?.(error as Error);
    }
  );
}
